/**
 * Noise, and the rules that make it trustworthy.
 *
 * What was here before: a chime played whenever the unread count went up, from a
 * 30-second poll, in every open tab, with no permission gate and no way to silence
 * it. Five tabs meant five chimes for one event, and the browser's own Do Not
 * Disturb had no say in any of it.
 *
 * The rules, in the order they matter:
 *
 *   1. ONE SOUND PER EVENT, NOT PER TAB. A claim is taken in localStorage before
 *      anything is announced, so the first tab to see a row is the only one that
 *      speaks. The claim is shared storage, not a leader election — good enough
 *      for staggered 30-second polls, and it degrades to the old behaviour rather
 *      than silence if storage is unavailable.
 *
 *   2. SOUND BELONGS TO THE LANE. Needs-you and security speak; the feed never
 *      does. That alone is most of the fix: the loud lane was the one carrying
 *      "an app read a node".
 *
 *   3. THE OS DECIDES WHEN. A real Notification honours Do Not Disturb and Focus,
 *      which an in-page chime can never do. That is the argument for asking
 *      permission at all.
 *
 *   4. NEVER ASK ON LOAD. A permission prompt with no context is refused once and
 *      then unavailable forever. It is offered next to the thing it is for.
 *
 *   5. THE PERSON'S SETTINGS DECIDE THE REST (GET /notifications/settings). A
 *      lane pops up only while its "This browser" switch is on, the chime only
 *      while Sound is on, and inside quiet hours neither happens — except for
 *      Security, which ignores them.
 */

const CLAIM_PREFIX = 'notif_seen:';
const CLAIM_TTL_MS = 10 * 60 * 1000;
const CHANNEL = 'paradigm-notifications';

// Lanes that are allowed to make a noise. The feed is deliberately absent.
export const LOUD_LANES = new Set(['queue', 'security']);

let channel = null;
try {
    channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null;
} catch { channel = null; }

const localClaims = new Set();

if (channel) {
    // Another tab got there first; take its word for it.
    channel.onmessage = (e) => { if (e?.data?.id) localClaims.add(e.data.id); };
}

/** True exactly once per notification id, across tabs. */
export function claim(id) {
    if (!id) return false;
    if (localClaims.has(id)) return false;

    const key = `${CLAIM_PREFIX}${id}`;
    try {
        if (localStorage.getItem(key)) { localClaims.add(id); return false; }
        localStorage.setItem(key, String(Date.now()));
        pruneClaims();
    } catch {
        // Private window, or storage disabled. Fall back to this tab's own memory:
        // one chime per tab beats none at all.
    }

    localClaims.add(id);
    try { channel?.postMessage({ id }); } catch { /* nothing to tell */ }
    return true;
}

function pruneClaims() {
    try {
        const cutoff = Date.now() - CLAIM_TTL_MS;
        for (let i = localStorage.length - 1; i >= 0; i -= 1) {
            const key = localStorage.key(i);
            if (!key?.startsWith(CLAIM_PREFIX)) continue;
            if (Number(localStorage.getItem(key)) < cutoff) localStorage.removeItem(key);
        }
    } catch { /* not worth a failure */ }
}

// ── permission ────────────────────────────────────────────────────────────────

export function permissionState() {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;           // 'default' | 'granted' | 'denied'
}

/** Ask. Only ever call this from a real user gesture, next to an explanation. */
export async function requestPermission() {
    if (typeof Notification === 'undefined') return 'unsupported';
    try {
        return await Notification.requestPermission();
    } catch {
        return Notification.permission;
    }
}

// ── the badge ─────────────────────────────────────────────────────────────────

let baseTitle = null;

/** Unread, on the tab and on the app icon where the platform allows it. */
export function setBadge(count) {
    if (typeof document !== 'undefined') {
        if (baseTitle === null) baseTitle = document.title.replace(/^\(\d+\)\s*/, '');
        document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
    }
    try {
        if (typeof navigator === 'undefined') return;
        if (count > 0) navigator.setAppBadge?.(count);
        else navigator.clearAppBadge?.();
    } catch { /* an unsupported badge is not a failure */ }
}

// ── the person's settings ─────────────────────────────────────────────────────

/** Whether this lane's "This browser" switch is on. Before the settings are read,
 *  the server's defaults: Needs you and Security pop up, Happened does not. */
export function popsUp(prefs, lane) {
    const own = prefs?.lanes?.nucleus?.[lane]?.browser;
    const byDefault = prefs?.defaults?.lanes?.nucleus?.[lane]?.browser;
    return own ?? byDefault ?? LOUD_LANES.has(lane);
}

/** Inside the person's quiet hours, read in their timezone (this browser's when
 *  they have none). The same window the server batches the feed by. */
export function inQuietHours(prefs, now = new Date()) {
    const start = prefs?.quiet_hours_start;
    const end = prefs?.quiet_hours_end;
    if (!start || !end) return false;
    const clock = (timeZone) => new Intl.DateTimeFormat('en-GB', {
        timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(now);
    let time;
    try { time = clock(prefs.timezone || undefined); } catch { time = clock(undefined); }
    return start <= end ? start <= time && time < end : time >= start || time < end;
}

// ── the announcement ──────────────────────────────────────────────────────────

/**
 * Announce rows that have just arrived.
 *
 * `rows` are unread notifications not seen before by this client. Returns what it
 * did, which is what the tests assert on — the DOM APIs themselves are not worth
 * mocking deeply.
 */
export function announce(rows, { playSound, onOpen, prefs = null, now } = {}) {
    const spoken = [];
    let chime = false;

    for (const row of rows || []) {
        if (row.lane !== 'security' && inQuietHours(prefs, now)) continue;
        const popUp = popsUp(prefs, row.lane) && permissionState() === 'granted';
        const loud = LOUD_LANES.has(row.lane);
        if (!popUp && !loud) continue;             // the feed stays quiet unless it may pop up
        if (!claim(row.id)) continue;              // another tab has this one

        spoken.push(row.id);
        if (loud) chime = true;

        if (popUp) {
            try {
                const toast = new Notification(row.title, {
                    body: row.message || '',
                    tag: row.id,                   // the OS collapses a repeat itself
                    silent: true,                  // the sound is ours to make, once
                });
                toast.onclick = () => {
                    try { window.focus(); } catch { /* not always allowed */ }
                    onOpen?.(row);
                    toast.close();
                };
            } catch { /* the OS refused; the sound below still lands */ }
        }
    }

    if (chime && prefs?.sound_enabled !== false) playSound?.();
    return spoken;
}

/** One pop-up now, so the person can see what one looks like. True if it showed. */
export function testPopUp() {
    if (permissionState() !== 'granted') return false;
    try {
        new Notification('Pop-ups work in this browser', {
            body: 'This is how ofSelf tells you something needs you.',
            tag: 'ofself-test',
        });
        return true;
    } catch {
        return false;
    }
}
